import type { Partner } from "@expert/shared-types";

import type { EvaluationDetailsBody } from "../api/evaluations/types";
import type { ChecklistCategory } from "./consts";

import {
    CALL_FLOW_UPDATED_CHECKLIST_CATEGORIES_ATT,
    CALL_FLOW_UPDATED_CHECKLIST_CATEGORIES_VERIZON,
    CHECKLIST_CATEGORIES,
} from "./consts";

/**
 * The updated call flow checklists are the standard for att and verizon; every other partner
 * still uses the original checklist.
 */
export function getChecklistCategories(partner: Partner): ChecklistCategory[] {
    switch (partner) {
        case "att":
            return CALL_FLOW_UPDATED_CHECKLIST_CATEGORIES_ATT;
        case "verizon":
            return CALL_FLOW_UPDATED_CHECKLIST_CATEGORIES_VERIZON;
        default:
            return CHECKLIST_CATEGORIES;
    }
}

function isChecklistBehaviorPresent(
    evaluationBehavior: string,
    presentBehaviors: readonly { behavior?: string }[],
): boolean {
    return presentBehaviors.some((present) =>
        present.behavior
            ? evaluationBehavior.toLocaleLowerCase().includes(present.behavior.toLocaleLowerCase())
            : false,
    );
}

export function getChecklistCategoryCount({
    category,
    behavior,
    presentBehaviors,
    partner,
}: {
    category: string;
    behavior: string;
    presentBehaviors: readonly { behavior?: string }[];
    partner: Partner;
}): { currentIndex: number; total: number } {
    const checklistCategories = getChecklistCategories(partner);
    const categoryItem = checklistCategories.find((cat) => cat.category === category);
    if (!categoryItem) {
        return { currentIndex: 0, total: 0 };
    }

    // Sale-detected rows render only when the evaluation returned them, in checklist order.
    // Number from that rendered list so a missing earlier row does not shift the page indicator.
    const renderedSaleBehaviors = categoryItem.saleDetectedBehaviors.filter((saleBehavior) =>
        isChecklistBehaviorPresent(saleBehavior.evaluationBehavior, presentBehaviors),
    );
    const total = categoryItem.behaviors.length + renderedSaleBehaviors.length;

    const primaryIndex = categoryItem.behaviors.findIndex((item) => item.evaluationBehavior === behavior);
    if (primaryIndex >= 0) {
        return { currentIndex: primaryIndex + 1, total };
    }

    const saleIndex = renderedSaleBehaviors.findIndex((item) => item.evaluationBehavior === behavior);
    return {
        currentIndex: saleIndex >= 0 ? categoryItem.behaviors.length + saleIndex + 1 : 0,
        total,
    };
}

export function sortBehaviorsByChecklist(evaluationResultsBehaviors: EvaluationDetailsBody[], partner: Partner) {
    const orderedBehaviors: (EvaluationDetailsBody & { category: string })[] = [];
    getChecklistCategories(partner).forEach((category) => {
        /**
         * Loop through each behavior in the category and
         * match it to the behaviors from evaluationResults
         */
        category.behaviors.forEach((behavior) => {
            const behaviorItem = evaluationResultsBehaviors.find((b) =>
                b.behavior
                    ? behavior.evaluationBehavior.toLocaleLowerCase().includes(b.behavior.toLocaleLowerCase())
                    : false,
            );

            if (behaviorItem) {
                orderedBehaviors.push({
                    ...behaviorItem,
                    category: category.category,
                });
            }
        });

        category.saleDetectedBehaviors.forEach((salesBehavior) => {
            const behaviorItem = evaluationResultsBehaviors.find((b) =>
                b.behavior
                    ? salesBehavior.evaluationBehavior.toLocaleLowerCase().includes(b.behavior.toLocaleLowerCase())
                    : false,
            );

            if (behaviorItem) {
                orderedBehaviors.push({
                    ...behaviorItem,
                    category: category.category,
                });
            }
        });
    });

    return orderedBehaviors;
}
